<template>
  <div>
    <div id="overlay" v-if="fetchingData">
      <div class="spinner"></div>
    </div>
    <h3>Revision Application</h3>
    <p>
      Below is the data that was provided initially for the original permit. Please complete the following form as it
      pertains to your project currently. All fees are non-refundable.
    </p>
    <dl class="row">
      <dt class="col-3" v-if="inspectionFee > 0">Inspection Fee:</dt>
      <dd class="col-9" v-if="inspectionFee > 0">${{inspectionFee}}</dd>
      <dt class="col-3">Application Fee:</dt>
      <dd class="col-9">${{applicationFee}}</dd>
    </dl>
    <collapsible-fieldset title="Contact Information"
                          :expanded="form.changesEmail"
                          :editing.sync="form.changesEmail">
      <p>
        All Cook County Department of environment and Sustainability Asbestos &amp; Demolition permits will be sent to permitees
        electronically via email. A valid email address must be supplied in order to receive information regarding the permit,
        including approved permits.
      </p>
      <div class="form-group">
        <label>
          Primary Email
        </label>
        <span class="form-text">
          The primary email address will be used for all communications and permits to be sent.
        </span>
      </div>
      <base-input v-model="form.emailForRevision"
                  :validation="$v.form.emailForRevision"
                  placeholder="Primary Email Address"
                  type="email" />
      <base-input v-model="form.confirmEmail"
                  :validation="$v.form.confirmEmail"
                  message="Emails must match"
                  placeholder="Confirm Email Address"
                  type="email" />
    </collapsible-fieldset>
    <collapsible-fieldset title="Site Information"
                          class="d-none"
                          :expanded="form.changesSiteInfo"
                          :editing.sync="form.changesSiteInfo">
      <div class="form-group">
        <label>Faciltiy Type</label>
        <div class="form-check">
          <input class="form-check-input" type="radio" v-model="form.facility.type" value="residential-single" id="fac-type-res-single">
          <label class="form-check-label" for="fac-type-res-single">
            Residential (Single-Family Home)
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" v-model="form.facility.type" value="residential" id="fac-type-res">
          <label class="form-check-label" for="fac-type-res">
            Residential (2-4 units)
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" v-model="form.facility.type" value="commercial" id="fac-type-commercial">
          <label class="form-check-label" for="fac-type-commercial">
            Commercial (includes Industrial, Residential 5 or more units)
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" v-model="form.facility.type" value="school" id="fac-type-school">
          <label class="form-check-label" for="fac-type-school">
            School
          </label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" v-model="form.facility.type" value="hospital" id="fac-type-hospital">
          <label class="form-check-label" for="fac-type-hospital">
            Hospital
          </label>
          <span class="form-text">
            For commercial, school, and hospital facilities, one application must be completed per building.
          </span>
        </div>
      </div>
      <div v-if="form.facility.type && form.facility.type.startsWith('residential')">
        <strong>How many of each type of building are being demolished? (If 0, leave blank)</strong>
        <div class="form-group row">
          <label for="houseCount" class="col-4 col-lg-2 col-form-label">House</label>
          <div class="col-8 col-lg-2">
            <input v-model.number="form.facility.houseCount"
                   type="number"
                   class="form-control"
                   id="houseCount"
                   placeholder="(i.e., 1)" />
          </div>
        </div>
        <div class="form-group row">
          <label for="garageCount" class="col-4 col-lg-2 col-form-label">Garage</label>
          <div class="col-8 col-lg-2">
            <input v-model.number="form.facility.garageCount"
                   type="number"
                   class="form-control"
                   id="garageCount"
                   placeholder="(i.e., 1)" />
          </div>
        </div>
        <div class="form-group row">
          <label for="shedCount" class="col-4 col-lg-2 col-form-label">Shed</label>
          <div class="col-8 col-lg-2">
            <input v-model.number="form.facility.shedCount"
                   type="number"
                   class="form-control"
                   id="shedCount"
                   placeholder="(i.e., 1)" />
          </div>
        </div>
      </div>
      <v-address v-model="form.facility.address"
                 labelPrefix="Facility Address"
                 :readonly="true"
                 :validation="$v.form.facility.address"
                 :show-unincorporated="true" />
      <div class="row align-items-end">
        <div class="col">
          <small>If there is asbestos present, asbestos must be removed/abated through the CCDES permitting process</small>
        </div>
        <div class="w-100"></div>
        <div class="col">
          <yes-no v-model="form.facility.asbestosPresent" label="Is asbestos present?" />
        </div>
      </div>
      <div class="row" v-if="form.facility.builtBefore1982">
        <div class="col">
          <base-file-uploader v-model="form.files.asbestosInspectionReport" label="Asbestos Inspection Report" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.permitNumber" label="Asbestos Permit # If Abated" />
        </div>
      </div>
      <div class="row" v-else>
        <div class="col">
          <small class="form-text text-muted">
            Attach supporting document showing year of building construction, such as construction permit, deed, appraisal date, etc.
          </small>
          <base-file-uploader v-model="form.files.supportingDocumentation"
                              label="Supporting Documentation" v-if="!form.facility.builtBefore1982" />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <base-input v-model="form.facility.contact"
                      label="Facility Owner Contact"
                      placeholder="Full Name"
                      :validation="$v.form.facility.contact" />
          <base-input v-model="form.facility.phone"
                      label="Facility Owner Phone"
                      type="tel"
                      mask="(###)-###-####"
                      placeholder="(xxx)-xxx-xxxx"
                      :validation="$v.form.facility.phone" />
          <base-input v-model="form.facility.email"
                      label="Facility Owner Email"
                      type="email"
                      placeholder="name@company.com"
                      :validation="$v.form.facility.email" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.contactName"
                      label="Facility Contact Name"
                      placeholder="Full Name"
                      :validation="$v.form.facility.contactName" />
          <base-input v-model="form.facility.contactPhone"
                      label="Facility Contact Phone"
                      type="tel"
                      mask="(###)-###-####"
                      placeholder="(xxx)-xxx-xxxx"
                      :validation="$v.form.facility.contactPhone" />
          <base-input v-model="form.facility.contactEmail"
                      label="Facility Contact Email"
                      type="email"
                      placeholder="name@company.com"
                      :validation="$v.form.facility.contactEmail" />
        </div>
      </div>
      <base-input v-model="form.facility.priorUse"
                  label="Facility Prior Use"
                  placeholder="Prior Use"
                  :validation="$v.form.facility.priorUse"
                  maxlength="50" />
      <base-input v-model="form.facility.presentUse"
                  label="Facility Present Use"
                  placeholder="Present Use"
                  :validation="$v.form.facility.presentUse"
                  maxlength="50" />
      <base-input v-model="form.facility.futureUse"
                  label="Facility Future Use"
                  placeholder="Future Use"
                  :validation="$v.form.facility.futureUse"
                  maxlength="50" />
      <div class="row">
        <div class="col">
          <base-input v-model="form.facility.buildingSize"
                      label="Building Size (Sq Ft)"
                      placeholder="Sq Ft"
                      :validation="$v.form.facility.buildingSize"
                      type="number" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.buildingFloors"
                      label="Building # of Floors"
                      placeholder="# of Floors"
                      :validation="$v.form.facility.buildingFloors"
                      type="number" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.constructionYear"
                      label="Building Construction Year"
                      placeholder="yyyy"
                      message="Construction year must be a valid year."
                      :validation="$v.form.facility.constructionYear"
                      mask="####" />
        </div>
        <div class="w-100"></div>
        <div class="col">
          <base-input v-model="form.facility.buildingName"
                      label="Building Name"
                      placeholder="Name"
                      :validation="$v.form.facility.buildingName"
                      maxlength="25" />
        </div>
      </div>
    </collapsible-fieldset>
    <!--
      <collapsible-fieldset title="Project Types"
                            :expanded="true"
                            v-if="this.form.omPermit == true">
        <div class="row">
          <div class="col">
            <label class="required" v-model="form.OMProjectTypeList"
                   :validation="$v.form.OMProjectTypeList">
              Type of Project: (Check all that apply)
            </label>
            <base-checkbox v-model="form.omFriableSchoolProjectType"
                           label="Friable School Project"
                           :validation="$v.form.friableSchool" />
            <base-checkbox v-model="form.omNonFriableSchoolProjectType"
                           label="Non-Friable School Floor Tile Project"
                           :validation="$v.form.nonFriableSchool" />
            <base-checkbox v-model="form.omCommercialProjectType"
                           label="Commercial Public building (Friable & Non-Friable)"
                           :validation="$v.form.commercialPublic" />
          </div>
          <div class="col">
            <label class="required" v-model="form.OMSubmittedList"
                   :validation="$v.form.OMSubmittedList">
              Submitted by:
            </label>
            <base-checkbox v-model="form.submitter.contractor"
                           label="Contractor"
                           :validation="$v.form.submitter.contractor"
                           v-on:click="setBoxes('contractor')" />
            <base-checkbox v-model="form.submitter.owner"
                           label="Owner"
                           :validation="$v.form.submitter.owner"
                           v-on:click="setBoxes('owner')" />
            <base-checkbox v-model="form.submitter.projectDesigner"
                           label="Project Designer"
                           :validation="$v.form.submitter.projectDesigner"
                           v-on:click="setBoxes('projectDesigner')" />
          </div>
        </div>
      </collapsible-fieldset>
      -->
    <collapsible-fieldset title="Facility Information"
                          :expanded="true">
      <v-address v-model="form.facility.address"
                 :readonly="true"
                 :validation="$v.form.facility" />
      <div class="row">
        <div class="col">
          <base-input v-model="form.facility.contactName"
                      label="Facility Contact Name"
                      :validation="$v.form.facility.contactName" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.contactPhone"
                      label="Facility Contact Phone"
                      :validation="$v.form.facility.contactPhone"
                      mask="(###)-###-####"
                      type="tel" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.contactEmail"
                      label=" Facility Contact Email"
                      :validation="$v.form.facility.contactEmail" />
        </div>
      </div>
    </collapsible-fieldset>
    <!--
    <collapsible-fieldset title="Facility Owner Information"
                          :expanded="true">
      <base-input v-model="form.facility.ownerOrSchool"
                  label="Facility Owner or School District"
                  :validation="$v.form.facility.ownerOrSchool" />
      <v-address v-model="form.facility.ownerAddress"
                 labelPrefix="Owner Address"
                 :validation="$v.form.facility.ownerAddress"
                 :show-unincorporated="false" />
      <div class="row">
        <div class="col">
          <base-input v-model="form.facility.contact"
                      label="Facility Owner Contact Name"
                      :validation="$v.form.facility.contact" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.phone"
                      label="Facility Owner Contact Phone"
                      :validation="$v.form.facility.phone"
                      mask="(###)-###-####"
                      type="tel" />
        </div>
        <div class="col">
          <base-input v-model="form.facility.email"
                      label=" Facility Owner Contact Email"
                      :validation="$v.form.facility.email" />
        </div>
      </div>
    </collapsible-fieldset>
    -->
    <collapsible-fieldset title="Contractor Information"
                          :expanded="form.changesContractorInfo"
                          :editing.sync="form.changesContractorInfo">
      <vue-simple-suggest :list="contractors"
                          v-model="form.contractor.name"
                          mode="input"
                          placeholder="Demolition Contractor Name"
                          value-attribute="id"
                          display-attribute="name"
                          @select="contractorSelected"
                          @blur="contractorBlur"
                          :filter-by-query="true">
        <label for="contractor-select"
               class="required">
          Contractor Name
        </label>
        <input id="contractor-select"
               v-model="form.contractorName"
               v-bind:class="{ 'is-invalid': $v.form.contractorName.$error }" />
        <div class="invalid-feedback">
          A valid contractor must be selected.
        </div>
      </vue-simple-suggest>
      <hr />
      <v-address v-model="form.contractor.address"
                 :validation="$v.form.contractor.address"
                 :readonly="contractorReadonly"
                 labelPrefix="Contractor" />
      <hr />
      <div class="row">
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractorContactFirstName"
                      label="Contractor Contact First Name"
                      placeholder="First Name"
                      :validation="$v.form.contractorContactFirstName" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractorContactLastName"
                      label="Contractor Contact Last Name"
                      placeholder="Last Name"
                      :validation="$v.form.contractorContactLastName" />
        </div>
        <div class="w-100"></div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractorContactPhone"
                      label="Contractor Phone"
                      type="tel"
                      placeholder="(xxx)-xxx-xxxx"
                      mask="(###)-###-####"
                      :validation="$v.form.contractorContactPhone" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.contractorContactEmail"
                      label="Contractor Email"
                      type="email"
                      placeholder="name@company.com"
                      :validation="$v.form.contractorContactEmail" />
        </div>
      </div>
    </collapsible-fieldset>
    <collapsible-fieldset title="Dates"
                          :expanded="form.changesPermitDates"
                          :editing.sync="form.changesPermitDates">
      <label>Project Dates</label>
      <div class="row">
        <div class="col">
          <base-date-picker v-model="form.workStartDate"
                            label="Start Date"
                            :validation="$v.form.workStartDate" />
        </div>
        <div class="col">
          <base-date-picker v-model="form.workEndDate"
                            label="End Date"
                            :validation="$v.form.workEndDate" />
        </div>
      </div>
      <label>Work Hours</label>
      <div class="row">
        <div class="col">
          <base-time-picker v-model="form.workHours.workHoursStart"
                            label="Start Time"
                            default-am-pm="AM"
                            :validation="$v.form.workHours.workHoursStart" />
        </div>
        <div class="col">
          <base-time-picker v-model="form.workHours.workHoursEnd"
                            label="End Time"
                            default-am-pm="PM"
                            :validation="$v.form.workHours.workHoursEnd" />
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <yes-no v-model="form.workHours.workWeekends" label="Working Weekends?" />
        </div>
      </div>
    </collapsible-fieldset>
    <collapsible-fieldset title="Asbestos Abatement Information"
                          v-if="form && form.projectType && form.projectType.toLowerCase() === 'asbestos'"
                          :expanded="form.changesAbatementInfo"
                          :editing="form.changesAbatementInfo">
      <div class="form-fields">
        <div class="form-group">
          <label class="required">What methods will be employed to remove the asbestos? (please check all applicable)</label>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" v-model="form.asbestosData.abatementHeat" value="heat" id="removal-type-heat">
            <label class="form-check-label" for="removal-type-heat">
              Heat
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="checkbox"
                   v-model="form.asbestosData.abatementGWR"
                   value="grosswet"
                   id="removal-type-grosswet">
            <label class="form-check-label" for="removal-type-grosswet">
              Gross Wet Removal
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="checkbox"
                   v-model="form.asbestosData.abatementInfrared"
                   value="infrared"
                   id="removal-type-infrared">
            <label class="form-check-label" for="removal-type-infrared">
              Infrared
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="checkbox"
                   v-model="form.asbestosData.abatementGlovebag"
                   value="glovebag" id="removal-type-glovebag">
            <label class="form-check-label" for="removal-type-glovebag">
              Glovebag
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="checkbox"
                   v-model="form.asbestosData.abatementSolvent"
                   value="solvent"
                   id="removal-type-solvent">
            <label class="form-check-label" for="removal-type-solvent">
              Solvent
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input"
                   type="checkbox"
                   v-model="form.asbestosData.abatementOther"
                   value="other"
                   id="removal-type-other">
            <label class="form-check-label" for="removal-type-other">
              Other (Please describe)
            </label>
          </div>
          <base-input v-model="form.asbestosData.asbestosRemovalMethodsOther"
                      :validation="$v.form.asbestosData.asbestosRemovalMethodsOther"
                      placeholder="Other abatement method"
                      :disabled="!form.asbestosData.abatementOther">
          </base-input>
        </div>
      </div>
      <base-textarea v-model="form.asbestosData.descriptionofACM"
                     label="Description of Asbestos Containing Materials being abated:">
      </base-textarea>
      <base-textarea v-model="form.asbestosData.locationofACM"
                     label="Description of locations of Asbestos Containing Materials (ACM) - please note what kind of
                     materials to expect at which locations. Be as specific as possible, including floor and room number.">
      </base-textarea>
      <div class="table-responsive">
        <table class="table table-striped table-bordered">
          <colgroup span="2"></colgroup>
          <colgroup span="2"></colgroup>
          <thead>
            <tr>
              <th rowspan="2"> </th>
              <th rowspan="2">RACM to be Removed</th>
              <th colspan="2">Non-friable Asbestos to be Removed</th>
              <th rowspan="2">Total Removed</th>
            </tr>
            <tr>
              <th scope="col">CAT I</th>
              <th scope="col">CAT II</th>
            </tr>
          </thead>
          <tr>
            <th scope="row">Pipes (Linear Ft)</th>
            <td>
              <base-input v-model.number="form.asbestosData.pipesRACM"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td>
              <base-input v-model.number="form.asbestosData.pipesNonFriableRemovalCat1"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td>
              <base-input v-model.number="form.asbestosData.pipesNonFriableRemovalCat2"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td :class="{'table-danger': checkSurfaceArea}">
              {{linearTotal}}
            </td>
          </tr>
          <tr>
            <th scope="row">Surface Area (Sq Ft)</th>
            <td>
              <base-input v-model.number="form.asbestosData.surfaceAreaRACM"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td>
              <base-input v-model.number="form.asbestosData.surfaceAreaNonFriableRemovalCat1"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td>
              <base-input v-model.number="form.asbestosData.surfaceAreaNonFriableRemovalCat2"
                          mask="#####"
                          min="0"
                          type="number" />
            </td>
            <td :class="{'table-danger': checkSurfaceArea}">
              {{squareTotal}}
            </td>
          </tr>
        </table>
      </div>
    </collapsible-fieldset>
    <collapsible-fieldset title="Disposal Site/Landfill Information"
                          :expanded="form.changesDisposalSite"
                          :editing="form.changesDisposalSite">
      <base-input v-model="form.disposalSite.name"
                  label="Disposal Site/Landfill Name"
                  :validation="$v.form.disposalSite.name" />
      <div class="row">
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.disposalSite.contact"
                      label="Disposal Site Contact"
                      placeholder="Full Name"
                      :validation="$v.form.disposalSite.contact" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.disposalSite.phone"
                      label="Disposal Site Phone"
                      mask="(###)-###-####"
                      :validation="$v.form.disposalSite.phone" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.disposalSite.email"
                      label="Disposal Site Email"
                      :validation="$v.form.disposalSite.email" />
        </div>
      </div>
      <hr />
      <v-address v-model="form.disposalSite.address"
                 :validation="$v.form.disposalSite.address"
                 :readonly="disposalSiteReadonly"
                 labelPrefix="Disposal Site" />
      <hr />
    </collapsible-fieldset>
    <collapsible-fieldset title="Waste Transporter Information"
                          :expanded="form.changesWasteTransporter"
                          :editing.sync="form.changesWasteTransporter">
      <base-input v-model="form.transporter.name"
                  label="Waste Transporter Name"
                  :validation="$v.form.transporter.name" />
      <div class="row">
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.transporter.contact"
                      label="Waste Transporter Contact"
                      placeholder="Full Name"
                      :validation="$v.form.transporter.contact" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.transporter.phone"
                      label="Waste Transporter Phone"
                      mask="(###)-###-####"
                      :validation="$v.form.transporter.phone" />
        </div>
        <div class="col-12 col-sm-6 col-lg">
          <base-input v-model="form.transporter.email"
                      label="Waste Transporter Email"
                      :validation="$v.form.transporter.email" />
        </div>
      </div>
      <hr />
      <v-address v-model="form.transporter.address"
                 :validation="$v.form.transporter.address"
                 :readonly="transporterReadonly"
                 labelPrefix="Transporter" />
      <hr />
    </collapsible-fieldset>
    <collapsible-fieldset title="School Contractor Information" v-if="this.form.facility.type == 'School' && this.form.omPermit == true"
                          :expanded="true"
                          :editing="true">
      <div class="row">
        <div class="col">
          <base-input v-model="form.projectDesignerName"
                      label="Project Designer Name"
                      :validation="$v.form.projectDesignerName"
                      v-on:change="canSubmit" />
        </div>
        <div class="col">
          <base-input v-model="form.projectDesignerId"
                      label="IDPH Number"
                      :validation="$v.form.projectDesignerId"
                      v-on:change="canSubmit" />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <base-input v-model="form.asbestosProjectManagerName"
                      label="Asbestos Project Manager Name"
                      :validation="$v.form.asbestosProjectManagerName"
                      v-on:change="canSubmit" />
        </div>
        <div class="col">
          <base-input v-model="form.asbestosProjectManagerId"
                      label="IDPH Number"
                      :validation="$v.form.asbestosProjectManagerId"
                      v-on:change="canSubmit" />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <base-input v-model="form.airSamplingProfessionalName"
                      label="Air Sampling Professional Name"
                      :validation="$v.form.airSamplingProfessionalName"
                      v-on:change="canSubmit" />
        </div>
        <div class="col">
          <base-input v-model="form.airSamplingProfessionalId"
                      label="IDPH Number"
                      :validation="$v.form.airSamplingProfessionalId"
                      v-on:change="canSubmit" />
        </div>
      </div>
    </collapsible-fieldset>
    <collapsible-fieldset title="Signature"
                          :expanded="true"
                          :editing="true">
      <base-input v-model="form.revisionSubmitterName"
                  label="Submitter's Name"
                  placeholder="Full Name"
                  :validation="$v.form.revisionSubmitterName" />
      <base-input v-model="form.title"
                  label="Submitter's Title"
                  :validation="$v.form.title" />
      <base-date-picker v-model="form.revisionSubmittalDate"
                        label="Date"
                        :readonly="true"
                        :validation="$v.form.revisionSubmittalDate" />
    </collapsible-fieldset>
    <div class="row mt-2 justify-content-end">
      <div class="col">
        <router-link :to="{name: 'RevisionSelect', params: {projectType: projectType, id: id}}"
                     class="btn btn-secondary">
          Choose Other Sections
        </router-link>
        <button type="button"
                class="btn btn-success  ml-auto"
                :disabled="!canSubmit"
                @click="submitRevision">
          &#10003; Submit
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CollapsibleFieldset from '~/components/CollapsibleFieldset';
import BaseInput from '~/components/BaseInput';
import BaseSelect from '~/components/BaseSelect';
import BaseTextarea from '~/components/BaseTexarea';
import BaseDatePicker from '~/components/BaseDatePicker';
import BaseTimePicker from '~/components/BaseTimePicker';
import BaseCheckbox from '~/components/BaseCheckbox';
import BaseFileUploader from '~/components/BaseFileUploader';
import YesNo from '~/components/YesNo';
import VAddress from '~/components/VAddress';
import cloneDeep from 'lodash/fp/cloneDeep';
import VueSimpleSuggest from 'vue-simple-suggest';

import { PermitApplication, RevisionApplication, Contractor } from '~/js/FormObjects';
import { RevisionApplicationValidation } from '@/js/FormValidations';
import { saveRevision, getContractors, getPrices } from '~/js/FormFunctions';

export default {
  name: 'Revision',
  props: ['id', 'projectType'],
  components: {
    CollapsibleFieldset,
    BaseInput,
    BaseSelect,
    BaseTextarea,
    BaseDatePicker,
    BaseTimePicker,
    BaseCheckbox,
    BaseFileUploader,
    YesNo,
    VAddress,
    VueSimpleSuggest,
  },
  mounted() {
    const self = this;
    const form = this.$store.getters.temporaryForm;
    if (!form) {
      this.$router.push({
        name: 'RevisionSelect',
        params: {
          projectType: this.projectType,
          id: this.id,
        },
      });
      return;
    }
    this.$set(this, 'form', cloneDeep(form));

    if (this.form.omPermit == true) {
      this.form.changesAbatementInfo = true;
      if (this.form.asbestosData.surfaceAreaRACM == null) {
        this.form.asbestosData.surfaceAreaRACM = 0;
        if (this.form.workStartDate == 'Mon Jan 01 0001 00:00:00 GMT-0550 (Central Standard Time)') {
          this.form.workStartDate = new Date();
          this.form.workStartDate.setDate(this.form.workStartDate.getDate() + 2);
        }
      }
    }

    this.contractors = this.$store.getters.contractors(this.form.projectType.toLowerCase());
    if (!this.contractors || this.contractors.length === 0) {
      this.fetchingData = true;
      getContractors(this.form.projectType.toLowerCase())
        .then((response) => {
          self.contractors = response;
          self.fetchingData = false;
        })
        .catch(() => {
          self.fetchingData = false;
        });
    }
    getPrices(this.projectType)
      .then((data) => {
        self.permitPrices = data;
      });
  },
  data() {
    return {
      fetchingData: false,
      form: new RevisionApplication(new PermitApplication('Demolition')),
      contractors: [],
      permitPrices: [],
    };
  },
  validations: {
    form: new RevisionApplicationValidation(),
  },
  methods: {
    submitRevision() {
      this.fetchingData = true;
      const formData = new FormData();
      const self = this;
      Object.keys(this.form.files).forEach((key) => {
        if (self.form.files[key]) {
          formData.append(key, self.form.files[key]);
        }
      });

      formData.append('form', JSON.stringify(this.form));
      if (this.form.omPermit == true) {
        saveRevision(formData)
          .then((response) => {
            self.$router.push({ name: 'OMThankYou', query: { feei: self.inspectionFeeAmount } });
            self.fetchingData = false;
          })
          .catch((error) => {
            console.error(error);
            self.fetchingData = false;
          });
      } else {
        saveRevision(formData)
          .then((response) => {
            self.$router.push({ name: 'Checkout', query: { revisionId: response.data } });
            self.fetchingData = false;
          })
          .catch((error) => {
            console.error(error);
            self.fetchingData = false;
          });
      }
    },
    showSchoolContractorInfo() {
      return (this.form.facility.type == 'School' && this.form.omPermit == true);
    },
    contractorSelected(contractor) {
      this.$v.form.contractorName.$touch();
      if (!(contractor) || !(contractor.id)) {
        // noinspection JSPrimitiveTypeWrapperUsage
        this.form.contractor = new Contractor('demolition');
        this.form.contractorName = null;
      } else {
        // noinspection JSPrimitiveTypeWrapperUsage
        this.form.contractor = contractor;
        this.form.contractorName = contractor.name;
        this.form.contractorContactFirstName = contractor.contactFirstName;
        this.form.contractorContactLastName = contractor.contactLastName;
        this.form.contractorContactPhone = contractor.contactPhone;
        this.form.contractorContactEmail = contractor.contactEmail;
      }
    },
    contractorBlur() {
      this.$v.form.contractorName.$touch();
      const vm = this;
      const contractor = this.contractors.find((c) => {
        if (vm.form.contractor && c.name === vm.form.contractor.name) {
          return c;
        }
        return null;
      });
      if (!contractor) {
        const newContractorName = this.form.contractor.name;
        // noinspection JSPrimitiveTypeWrapperUsage
        this.form.contractor = new Contractor('demolition');
        this.form.contractor.name = newContractorName;
        this.form.contractorName = newContractorName;
      }
    },
  },
  watch: {
    linearTotal(val) {
      this.form.asbestosData.linearftTotalRemoval = val;
    },
    squareTotal(val) {
      this.form.asbestosData.surfaceAreaTotalRemoval = val;
    },
    'form.changesWasteTransporter': function (val) {
      if (val) {
        this.$v.form.transporter.$touch();
      }
    },
    'form.changesDisposalSite': function (val) {
      if (val) {
        this.$v.form.disposalSite.$touch();
      }
    },
    'form.changesEmail': function (val) {
      if (val) {
        this.$v.form.emailForRevision.$touch();
        this.$v.form.confirmEmail.$touch();
      }
    },
    'form.changesContractorInfo': function (val) {
      if (val) {
        this.$v.form.contractor.$touch();
      }
    },
  },
  computed: {
    linearTotal() {
      if (this.form.asbestosData) {
        const ad = this.form.asbestosData;
        return (ad.pipesRACM || 0) + (ad.pipesNonFriableRemovalCat1 || 0) + (ad.pipesNonFriableRemovalCat2 || 0);
      }
      return 0.00;
    },
    checkSurfaceArea() {
      if (this.form.projectType.toLowerCase() === 'demolition') {
        return true;
      }
      const linearTotal = parseInt(this.form.asbestosData.linearftTotalRemoval, 10);
      const surfaceTotal = parseInt(this.form.asbestosData.surfaceAreaTotalRemoval, 10);
      return !(linearTotal + surfaceTotal > 0);
    },
    squareTotal() {
      if (this.form.asbestosData) {
        const ad = this.form.asbestosData;
        return (ad.surfaceAreaRACM || 0) + (ad.surfaceAreaNonFriableRemovalCat1 || 0) + (ad.surfaceAreaNonFriableRemovalCat2 || 0);
      }
      return 0.00;
    },

    contractorReadonly() {
      return (this.form.projectType.toLowerCase() === 'asbestos') && !this.form.omPermit;
    },
    transporterReadonly() {
      return (this.form.projectType.toLowerCase() === 'asbestos') && !this.form.omPermit;
    },
    disposalSiteReadonly() {
      return (this.form.projectType.toLowerCase() === 'asbestos') && !this.form.omPermit;
    },
    inspectionFee() {
      let price = 0.00;
      if (!this.permitPrices || this.permitPrices.length === 0) {
        return price;
      }
      const facilityType = this.form.facility.type && this.form.facility.type.startsWith('Residential') ? 'residential' : 'commercial';
      if (this.form.projectType.toLowerCase() === 'demolition' || facilityType === 'residential') {
        return price;
      }

      const inspectionFees = this.permitPrices
        .filter(p => p.permitType === 'commercial')
        .filter(p => p.feeType === 'inspection');

      const squareFeetPrice = inspectionFees
        .filter(p => p.unitType === 'square_feet')
        .map(p => p.price)[0];
      const linearFeetPrice = inspectionFees
        .filter(p => p.unitType === 'linear_feet')
        .map(p => p.price)[0];

      price += this.form.asbestosData.surfaceAreaTotalRemoval * squareFeetPrice;
      price += this.form.asbestosData.linearftTotalRemoval * linearFeetPrice;
      price = Math.min(2000.00, price);
      price -= this.form.inspectionFeeAmount;
      price = Math.max(price, 0.00);
      this.inspectionFeeAmount = price;
      return price;
    },
    applicationFee() {
      const price = 0.00;
      if (!this.permitPrices || this.permitPrices.length === 0 || this.form.omPermit == true || this.form.projectHold || this.form.projectCancel) {
        return price;
      }

      return this.permitPrices
        .filter(p => p.feeType === 'revision')
        .map(p => p.price)[0];
    },
    canSubmit() {
      const formVal = this.$v.form;
      let valid = !(formVal.revisionSubmitterName.$invalid || formVal.title.$invalid || formVal.signDate.$invalid);
      if (valid != true) {
        return valid;
      }
      if (this.form.changesAbatementInfo) {
        valid = valid && !formVal.asbestosData.$anyError;
      }
      if (this.form.changesContractorInfo) {
        valid = valid && !formVal.contractor.$anyError;
      }
      if (this.form.changesDisposalSite) {
        valid = valid && !formVal.disposalSite.$anyError;
      }
      if (this.form.changesEmail) {
        valid = valid && !formVal.emailForRevision.$error;
        valid = valid && !formVal.confirmEmail.$error;
      }
      if (this.form.changesPermitDates) {
        valid = valid && !formVal.workStartDate.$error && !formVal.workEndDate.$error;
        valid = valid && !formVal.workHours.workHoursStart.$error && !formVal.workHours.workHoursEnd.$error;
      }
      if (this.form.changesWasteTransporter) {
        valid = valid && !formVal.transporter.$anyError;
      }
      if (this.form.facility.type === 'School' && (this.form.projectDesignerName === '' || this.form.projectDesignerId === ''
          || this.form.asbestosProjectManagerName === '' || this.form.asbestosProjectManagerId === ''
          || this.form.airSamplingProfessionalName === '' || this.form.airSamplingProfessionalId === '') && this.form.omPermit) {
        valid = false;
      }
      if (this.form.asbestosData.abatementHeat == false && this.form.asbestosData.abatementGWR == false
          && this.form.asbestosData.abatementInfrared == false && this.form.asbestosData.abatementGlovebag == false
          && this.form.asbestosData.abatementSolvent == false && this.form.asbestosData.abatementOther == false && this.form.omPermit) {
        valid = false;
      }
      if (this.form.asbestosData.abatementOther == true && (this.form.asbestosData.asbestosRemovalMethodsOther == '' || this.form.asbestosData.asbestosRemovalMethodsOther == null) && this.form.omPermit) {
        valid = false;
      }
      if (this.checkSurfaceArea == true && this.form.omPermit) {
        valid = false;
      }
      return valid;
    },
  },
};
</script>
